/**
 * Creates a base data provider for Gainsight PX that can be reused across apps
 * This handles the common data fetching from Redux state (user and PSAP data)
 *
 * Expected Redux state shape:
 * {
 *   sinatra: {
 *     user: {
 *       profile: { id, email, first_name, last_name, date_joined, last_active_session },
 *       currentRole: { application, name }
 *     }
 *   },
 *   psaps: {
 *     currentPSAP: {
 *       account_id, display_name, contact_email, active_capabilities,
 *       population, state, dispatch_type
 *     }
 *   },
 *   emergencyCalls: {
 *     selectedCallerID: string,
 *     callQueue: {
 *       [callerId]: {
 *         core: {
 *           locations: [{ extra_info: { payload_id }, call_start_time }],
 *           secondaryLocations: [{ extra_info: { payload_id }, call_start_time }]
 *         }
 *       }
 *     }
 *   }
 * }
 */
export function createBaseGainsightProvider(store) {
  return {
    getUserData: () => {
      const user = store?.getState()?.sinatra?.user;
      if (!user) return null;

      return {
        profileId: user.profile?.id,
        application: user.currentRole?.application,
        email: user.profile?.email,
        firstName: user.profile?.first_name,
        lastName: user.profile?.last_name,
        dateJoined: user.profile?.date_joined,
        lastActiveSession: user.profile?.last_active_session,
        role: user.currentRole?.name
      };
    },

    getAccountData: () => {
      const psap = store?.getState()?.psaps?.currentPSAP;
      if (!psap) return null;

      return {
        accountId: psap.account_id,
        displayName: psap.display_name,
        contactEmail: psap.contact_email,
        capabilities: psap.active_capabilities,
        population: psap.population,
        state: psap.state ?? psap.agency_state,
        dispatchType: psap.dispatch_type
      };
    },

    getPSAPData: () => {
      const psap = store?.getState()?.psaps?.currentPSAP;
      if (!psap) return null;

      return {
        accountId: psap.account_id
      };
    },

    getSelectedCallData: () => {
      const state = store?.getState();
      if (!state?.emergencyCalls) return null;

      const selectedCallerID = state.emergencyCalls.selectedCallerID;
      if (!selectedCallerID || !state.emergencyCalls.callQueue) return null;

      const call = state.emergencyCalls.callQueue[selectedCallerID];
      if (!call?.core) return null;

      const { locations, secondaryLocations } = call.core;
      const firstLoc = locations?.[0] || secondaryLocations?.[0];
      if (!firstLoc) return null;

      return {
        payloadId: firstLoc?.extra_info?.payload_id,
        callStartTime: firstLoc?.call_start_time
      };
    }
  };
}
